import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React from 'react'
import Theme from 'core/themes/model'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import TextField from './TextField'

interface Props {
  id?: string
  onChange: (value: number) => void
  value?: number
  className?: string
  disabled?: boolean
  iconSize?: 'sm' | 'lg'
  min?: number
  max?: number
  allowTypedInput?: boolean
}

export default function QuantitySelector({
  id,
  value,
  onChange,
  className,
  disabled = false,
  iconSize = 'sm',
  min = 0,
  max,
  allowTypedInput = true,
}: Props) {
  const classes = useStyles()
  const [inputValue, setInputValue] = React.useState(value)

  const decrement = () => {
    const newValue = inputValue - 1 < min ? inputValue : inputValue - 1
    onChange(newValue)
    setInputValue(newValue)
  }

  const increment = () => {
    const newValue = max && inputValue + 1 > max ? max : inputValue + 1
    onChange(newValue)
    setInputValue(newValue)
  }

  const handleInputChange = (newValue) => {
    setInputValue(newValue)
    onChange(newValue)
  }

  return (
    <div
      className={clsx(
        classes.quantitySelector,
        disabled && classes.quantitySelectorDisabled,
        className,
      )}
      data-testid="quantity-selector"
    >
      <button
        type="button"
        className={clsx(classes.button, disabled && classes.buttonDisabled, 'button')}
        onClick={decrement}
        data-testid="decrement-btn"
      >
        <FontAwesomeIcon
          size={iconSize}
          className={clsx(classes.icon, disabled && classes.iconDisabled)}
        >
          minus
        </FontAwesomeIcon>
      </button>
      <TextField
        id={id}
        data-testid="quantity-input-field"
        type="number"
        value={inputValue}
        className={classes.input}
        onChange={handleInputChange}
        min={min}
        disabled={disabled}
        nonce={undefined}
        onResize={undefined}
        onResizeCapture={undefined}
        enterKeyHint={undefined}
      />
      <button
        type="button"
        className={clsx(classes.button, disabled && classes.buttonDisabled, 'button')}
        onClick={increment}
        data-testid="increment-btn"
      >
        <FontAwesomeIcon
          size={iconSize}
          className={clsx(classes.icon, disabled && classes.iconDisabled)}
        >
          plus
        </FontAwesomeIcon>
      </button>
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  quantitySelector: {
    display: 'grid',
    gridTemplateColumns: '30% 40% 30%',
    height: 'min-content',
    width: '127px',
    border: `1px solid ${theme.palette.grey[200]}`,
    transition: 'all .2s ease',
    backgroundColor: theme.palette.common.white,
    borderRadius: '4px',
    cursor: 'pointer',
  },
  quantitySelectorDisabled: {
    backgroundColor: theme.palette.grey[50],
    cursor: 'not-allowed',
  },
  button: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: 'none',
    backgroundColor: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.components.selectableCard.activeBackground,
    },
    borderRadius: '4px',
  },
  buttonDisabled: {
    backgroundColor: theme.palette.grey[50],
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  input: {
    minWidth: '32px',
    maxWidth: '70px',
    gap: '0px',
    '& .inputFrame': {
      borderTop: 'none',
      borderBottom: 'none',
      borderLeft: `1px solid ${theme.palette.grey[200]}`,
      borderRight: `1px solid ${theme.palette.grey[200]}`,
      borderRadius: 0,
    },
    '& .input': {
      textAlign: 'center',
      '-moz-appearance': 'textfield', // hide increment/decrement arrows in Firefox
    },
    // Hide the up and down arrows in all browsers
    '& .input::-webkit-inner-spin-button, & .input::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '& .input::-ms-clear': {
      display: 'none',
    },
  },
  icon: {
    color: theme.palette.grey[900],
  },
  iconDisabled: {
    color: theme.palette.grey[300],
  },
}))
